/**
 * Contentsquare (formerly Hotjar) session replay and behavior analytics.
 *
 * Replaces Lucky Orange. See `.claude/plans/hotjar-migration-prd.md` for why,
 * and the migration plan for the phased rollout. Lucky Orange still runs
 * alongside this during the verification window.
 *
 * Three rules this module exists to enforce:
 *
 * 1. **One gate.** `isContentsquareEnabled()` governs both script loading and
 *    identification. Lucky Orange gates the script on a hostname check in
 *    index.html and identification on `import.meta.env.PROD` in app.tsx —
 *    two gates that can disagree. There is exactly one here.
 *
 * 2. **Contact fields are sent for Support search — a deliberate PII decision.**
 *    Phone, email, name, and company are sent so Support can find a session
 *    directly from a ticket (Contentsquare segment on the custom variable,
 *    "has a value which contains X"). This reverses an earlier no-contact-PII
 *    posture: the tool is internal-only, and phone in particular is often a
 *    producer's only reliable handle (email/name are optional for Basic-role
 *    users). Contentsquare applies no automatic redaction to these, so every
 *    field here is a conscious exposure — keep the list to what Support
 *    actually searches on. Phone is normalized to digits so formatting can't
 *    break a match.
 *
 * 3. **Sessions are metered, so who gets recorded is a decision.** The tag is
 *    not injected until auth resolves, because until then we cannot tell a
 *    processor (always recorded) from a staff member (never recorded) from an
 *    anonymous marketplace browser (sampled). See ANONYMOUS_SAMPLE_RATE.
 */

import { environment } from 'environments/environment';
import { isPlaywrightTestMode } from 'lib/test-mode';

declare global {
  interface Window {
    /** Contentsquare command queue, consumed by the tag once it loads. */
    _uxa: unknown[];
    /** Defined by the Contentsquare tag itself once present. */
    CS_CONF?: unknown;
  }
}

/**
 * Custom variable slots. Contentsquare allows indexes 1–20 per project, and
 * the index is the identity of the variable — renumbering silently
 * reinterprets historical data, so treat these as append-only.
 *
 */
const CUSTOM_VAR = {
  userId: 1,
  role: 2,
  vendorId: 3,
  vendorName: 4,
  internal: 5,
  userType: 6,
  email: 7,
  phone: 8,
  name: 9,
  company: 10,
} as const;

/** Visit scope — persists across pageviews within a session. */
const SCOPE_VISIT = 'visit';

/**
 * Email domains whose users are never recorded. Farmshare staff sign in with
 * `@farmshare.co` addresses; their sessions are internal noise, not customer
 * behavior, and shouldn't consume replay volume or bank staff PII. On identify
 * we opt these users OUT (see below) rather than merely flagging them, so the
 * exclusion is at collection time, not just analysis time.
 *
 * This is distinct from — and complementary to — the `internal` custom variable
 * (slot 5), which marks *impersonation* sessions we deliberately DO record so
 * Support can watch its own reproductions. A real staff member browsing as
 * themselves is caught here and never recorded at all.
 */
const INTERNAL_EMAIL_DOMAINS = ['farmshare.co'];

/**
 * True when an email belongs to an internal domain. The leading `@` anchors the
 * match so `x@notfarmshare.co` does not count as `@farmshare.co`. Case-folded
 * because email domains are case-insensitive.
 */
export function isInternalEmail(email?: string | null): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return INTERNAL_EMAIL_DOMAINS.some((domain) => lower.endsWith(`@${domain}`));
}

/**
 * Share of anonymous (logged-out) visitors that get recorded.
 *
 * Contentsquare bills by session, and every public route — the marketplace,
 * the home page, /signup, /faq, the scheduling landing pages — renders inside
 * `<App />`, so each guest and each JS-executing crawler costs a session.
 * Those sessions carry no userId, role, or vendor, which makes them
 * unsearchable for Support and thin for anything but marketplace funnels.
 *
 * The rationing is deliberately asymmetric, and that asymmetry is the whole
 * reason this lives in code rather than in Contentsquare's project settings:
 * the vendor's own sampling rate applies uniformly, which would mean three out
 * of four support tickets have no replay to find. Signed-in users are recorded
 * at 100%; anonymous traffic is the only thing sampled.
 *
 * Tune this number to move anonymous volume. Because the bucket is derived
 * from a stored id rather than stored as a decision, a changed rate re-buckets
 * returning visitors too, instead of only applying to new ones.
 */
const ANONYMOUS_SAMPLE_RATE = 0.2;

/**
 * Public pages that never START a recording.
 *
 * Contentsquare bills per session, not per page, and once the tag is on the
 * page it records every route the visitor goes on to. So a page cannot be
 * "skipped" mid-session; the only saving available is not starting one. A
 * visitor who only ever sees these pages costs nothing, and one who moves on to
 * a page we do record is recorded from that point — including any of these
 * pages they return to afterwards.
 *
 * These are the marketing and discovery pages: no Support ticket needs their
 * replay, and none of them identify anyone. The booking flow
 * (`/scheduling/:slug`, `/scheduling/:id/details`) is deliberately NOT here —
 * that is where a producer books a job, and it is a Support topic.
 *
 * Routes outside `<App />` (`/login`, `/kiosk/:token`, `/promos`) never load
 * the tag in the first place, so they need no entry.
 */
const UNRECORDED_PATH_PATTERNS: RegExp[] = [
  /^\/$/,
  /^\/marketplace(\/|$)/,
  /^\/faq\/?$/,
  /^\/compare\/?$/,
  /^\/scheduling\/landing\//,
  /^\/processors\/[^/]+\/?$/,
  /^\/buyer\/processor\/[^/]+\/?$/,
  /^\/waitlist\/unsubscribe\//,
  /^\/buyer\/interest\/unsubscribe\//,
];

/** Whether a recording may start on this path. See UNRECORDED_PATH_PATTERNS. */
export function isContentsquareRecordablePath(pathname: string): boolean {
  return !UNRECORDED_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * How long to wait for the tag to answer a replay-link request before giving
 * up. Generous: the tag answers in milliseconds when it is there at all, so
 * this only ever elapses when it is not coming.
 */
const REPLAY_LINK_TIMEOUT_MS = 2_000;

/**
 * localStorage key holding a random per-browser id, used only to derive the
 * sampling bucket. It is never sent to Contentsquare and never joined to a
 * user — persisting it keeps a sampled-in visitor sampled in across visits, so
 * a returning guest's journey stays whole rather than appearing in fragments.
 */
const ANONYMOUS_VISITOR_KEY = 'cs_anonymous_visitor_id';

/**
 * Stable random id for this browser, or null when storage is unavailable
 * (Safari private mode, a sandboxed iframe, or site data blocked) — every
 * access is wrapped because those cases *throw* rather than return null.
 */
function anonymousVisitorId(): string | null {
  try {
    const existing = window.localStorage.getItem(ANONYMOUS_VISITOR_KEY);
    if (existing) return existing;

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    window.localStorage.setItem(ANONYMOUS_VISITOR_KEY, id);
    return id;
  } catch {
    return null;
  }
}

/**
 * FNV-1a folded into [0, 1). Any stable hash would do; the requirement is that
 * the same id always lands in the same place, so the sample is a fixed subset
 * of visitors rather than a coin flip per page load.
 */
function hashToUnitInterval(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    // Math.imul keeps the multiply in 32-bit space; `*` would lose precision.
    hash = Math.imul(hash, 0x01000193);
  }
  // `>>> 0` reads the result as unsigned before scaling to [0, 1).
  return (hash >>> 0) / 0x100000000;
}

/** Whether this anonymous visitor falls inside ANONYMOUS_SAMPLE_RATE. */
function isAnonymousVisitorSampled(): boolean {
  const id = anonymousVisitorId();
  // No storage means no stable id, so fall back to a per-page-load roll: the
  // overall rate holds, and a browser that cannot persist an id could not have
  // been kept in a consistent bucket anyway.
  if (!id) return Math.random() < ANONYMOUS_SAMPLE_RATE;
  return hashToUnitInterval(id) < ANONYMOUS_SAMPLE_RATE;
}

/**
 * Session-replay masking.
 *
 * Contentsquare masks form *inputs* by default but records rendered page
 * content — so a submitted cutsheet showing a customer's name, phone, and
 * address is captured unless we mask it. Masking is **not retroactive**:
 * tightening it later only affects future sessions, and anything recorded
 * under a leakier posture is banked until it ages out. The safe direction of
 * change is therefore loosen-later, and this list should start broad.
 *
 * Two ways to add coverage, both non-destructive to widen:
 *   - Put `data-cs-private` on any element whose rendered text is sensitive.
 *   - Add a CSS selector here for a whole region.
 *
 * Refine with concrete cutsheet/customer selectors once real replays show
 * what's actually on screen. See PRO-638 #4.
 */
const MASK_SELECTORS = ['[data-cs-private]'];

/**
 * Read via `environments/environment` rather than `import.meta.env` directly:
 * Jest swaps that module for a CJS-safe stub, which is the established way this
 * codebase keeps Vite env access testable.
 */
const TAG_ID = environment.contentsquareTagId;

/**
 * Whether we have identified a user this page load. Gates `resetIdentity` so it
 * fires only on a genuine logout (identified → null), never on the initial
 * undefined-while-auth-loads state that occurs on every page load.
 */
let hasIdentified = false;

/**
 * Whether we have committed to recording this page load. Latches on the first
 * injection and is never cleared: the decision is per page load, and a visitor
 * who signs in mid-visit keeps the session that is already running.
 */
let hasRequestedLoad = false;

/**
 * The path the tag recorded on injection, until the next pageview is tracked.
 * Lets `trackContentsquarePageview` skip a duplicate of the starting page.
 */
let initialPath: string | null = null;

type ContentsquareUser = {
  _id: string;
  role?: string | null;
  active_vendor?: { _id?: string | null; shop_name?: string | null } | null;
  // Contact fields for Support search — all optional (Basic-role producers may
  // have none of email/name; company or phone can be the only handle).
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
};

type IdentifyOptions = {
  /**
   * True when an admin is impersonating this user. Marks the session internal
   * so Product can exclude staff behavior from funnels and Support can find
   * its own reproductions. A `role: Admin` user browsing as themselves is
   * caught by role; impersonation needs its own flag because the session is
   * identified as the *customer* while being driven by staff.
   */
  impersonating?: boolean;

  /**
   * Persona for Basic-role portal users: 'producer' | 'buyer' | 'both'.
   * Producers, buyers, and dual-role users all share the Basic role, so role
   * alone can't separate the two sides of the portal (producers access jobs,
   * buyers access splits). Derived at the call site from the app's existing
   * buyer-detection helpers so it can't diverge from what the app renders.
   * Undefined (and so omitted) for non-Basic users like processors.
   */
  userType?: string;

  /**
   * Whether to send the active vendor (`vendorId` / `vendorName`). True only for
   * real processor accounts — the users who actually *own* a vendor. Producers
   * and buyers get an arbitrary "first vendor in their tenant" from the session
   * fallback, and admins (internal team) get whatever vendor they're viewing;
   * sending either would pollute vendor-filtered processor funnels with sessions
   * that don't belong to that vendor. Decided at the call site via the app's
   * `isProcessor`/`isAdmin` helpers.
   */
  includeVendor?: boolean;
};

/**
 * The single gate. Production only.
 *
 * Sentry's init reads `VITE_ENV` too, but enables on *any* environment — this
 * is a value comparison rather than a truthiness test because Contentsquare is
 * deliberately production-only.
 *
 * The test-mode check is belt-and-braces: `isPlaywrightTestMode()` is
 * `!PROD`-guarded and so is already false in production builds, but a
 * Playwright run should never produce a session even if it is somehow pointed
 * at a production-flagged build.
 */
export function isContentsquareEnabled(): boolean {
  if (isPlaywrightTestMode()) return false;
  if (environment.env !== 'production') return false;
  return Boolean(TAG_ID);
}

/**
 * Queues a command for the tag, unless we have decided not to record this
 * visitor.
 *
 * The recording guard is not bookkeeping — `window._uxa` is a plain global
 * array that ANY Contentsquare tag drains on arrival, including one this
 * module never loaded. Production currently carries exactly such a tag (see
 * `hasForeignTagScript`), so a pageview left in the queue for a visitor we
 * declined to record is a pageview that tag will replay into a real session.
 * The queue reset in `loadContentsquare` only covers this module's own
 * injection; this covers everything else.
 */
function push(command: unknown[]): void {
  if (!isContentsquareEnabled()) return;
  if (!hasRequestedLoad) return;
  window._uxa = window._uxa || [];
  window._uxa.push(command);
}

/**
 * Queues a command that must reach any tag on the page even when we chose not
 * to load one ourselves.
 *
 * Only suppression belongs here. `optout` is the case: staff are excluded by
 * never injecting the tag, so `hasRequestedLoad` is false for them — but if
 * another loader put a tag on the page, that tag is recording a staff member
 * right now and the opt-out is the only thing that stops it. A guard that
 * swallowed it would turn a foreign tag into an un-silenceable one.
 */
function pushSuppression(command: unknown[]): void {
  if (!isContentsquareEnabled()) return;
  window._uxa = window._uxa || [];
  window._uxa.push(command);
}

/**
 * Whether a Contentsquare tag script is already in the document that this
 * module did not put there — a GTM-managed copy, for instance. Only meaningful
 * before our own injection, which is the only place it is consulted.
 */
function hasForeignTagScript(): boolean {
  return document.querySelector('script[src*="contentsquare"]') !== null;
}

/** Path in the form Contentsquare expects, with the hash folded into a query. */
function currentPath(): string {
  return window.location.pathname + window.location.hash.replace('#', '?__');
}

/**
 * Injects the Main Tracking Tag, if this visitor is one we record.
 *
 * **Call only once auth has resolved**, passing the resolved user (or null for
 * a genuine anonymous visitor) — never the undefined-while-loading state. The
 * decision this makes cannot be revisited: a session begins the moment the tag
 * arrives, so injecting before we know who this is spends a session on every
 * staff member and every crawler that reaches the public marketplace.
 *
 * The cost of waiting is the first moments of the recording. That is real, and
 * it is the price of the three-way split: signed-in users always, internal
 * staff never, anonymous visitors sampled (see ANONYMOUS_SAMPLE_RATE).
 *
 * Safe to call repeatedly — the first injection latches.
 * @param user the resolved user, or null/undefined for an anonymous visitor
 */
export function loadContentsquare(user?: ContentsquareUser | null): void {
  if (!isContentsquareEnabled()) return;
  if (hasRequestedLoad) return;

  // Staff are excluded by never loading the tag at all, rather than by the
  // `optout` in identify. That opt-out still matters — it covers a session
  // already recording when staff sign in — but it can only fire after the tag
  // has loaded and therefore after a session already exists. Not loading is
  // the version with no session to pay for.
  if (isInternalEmail(user?.email)) return;

  // Not latched: app.tsx calls again when the visitor reaches a page we record.
  if (!isContentsquareRecordablePath(window.location.pathname)) return;

  if (!user?._id && !isAnonymousVisitorSampled()) return;

  hasRequestedLoad = true;

  // Another loader got here first. `CS_CONF` only exists once a tag has
  // EXECUTED, so it loses the race against a script that is still in flight —
  // which is how production ended up fetching the 160 kB tag twice and running
  // two recording instances (one unattributed, one from this line). Checking
  // the DOM as well catches the in-flight case.
  //
  // Queue the masking anyway: a tag injected by something other than this
  // module was injected with no PII selectors, and nothing else will send
  // them. It is not retroactive — frames recorded before this lands are
  // already captured — but it is strictly better than never sending it.
  if (typeof window.CS_CONF !== 'undefined' || hasForeignTagScript()) {
    window._uxa = window._uxa || [];
    window._uxa.push(['setPIISelectors', { PIISelectors: MASK_SELECTORS }]);
    return;
  }

  // Start the queue clean. Anything already in it was pushed while we had
  // decided NOT to record — pageviews from an anonymous visitor outside the
  // sample who has since signed in, say. The tag drains whatever it finds on
  // arrival, so leaving them would open the session with a replay of browsing
  // that was never recorded.
  window._uxa = [];

  // Queue masking BEFORE the tag script is injected so it is in force from the
  // first recorded frame — there is no post-hoc redaction, so a masking config
  // that lands a beat late means real PII in early sessions.
  window._uxa.push(['setPIISelectors', { PIISelectors: MASK_SELECTORS }]);

  window._uxa.push(['setPath', currentPath()]);
  initialPath = currentPath();

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://t.contentsquare.net/uxa/${TAG_ID}.js`;
  document.head.appendChild(script);
}

/**
 * Records an artificial pageview after a client-side route change.
 *
 * Without this the tag only ever sees the initial load, and every subsequent
 * screen in this SPA is invisible to replays, heatmaps, and funnels.
 */
export function trackContentsquarePageview(): void {
  // A recording that starts on a client-side navigation already counts the
  // page it started on (via `setPath`); the route-change effect for that same
  // navigation would otherwise record it twice.
  const path = currentPath();
  const startedHere = path === initialPath;
  initialPath = null;
  if (startedHere) return;
  push(['trackPageview', path]);
}

/**
 * Associates the session with a Farmshare user, and re-associates it whenever
 * identity changes. There is deliberately no once-per-page-load guard: the
 * stale-attribution bug in the Lucky Orange integration is exactly that guard,
 * and it makes vendor switches and impersonation transitions unattributable.
 *
 * The user ID is sent three ways on purpose, because which of them Contentsquare
 * will actually let us filter Session Replay by is unresolved on the Growth
 * tier — the trial is meant to answer it. Once known, drop the ones that don't
 * work rather than leaving all three in place.
 *
 * Impersonation is re-identified in place, not split into a new session: an
 * admin's reproduction is one continuous replay Support wants to watch whole,
 * so we re-identify from admin to impersonated user without `resetIdentity`.
 * The `internal` flag is what separates it from a real customer session.
 */
export function identifyContentsquareUser(
  user?: ContentsquareUser | null,
  {
    impersonating = false,
    userType,
    includeVendor = false,
  }: IdentifyOptions = {},
): void {
  if (!isContentsquareEnabled()) return;

  if (!user?._id) {
    // Distinguish a real logout from the initial "not loaded yet" state.
    // `userState` is undefined while auth resolves, so this runs on every page
    // load before the user arrives — and `resetIdentity` resets the session,
    // which we must not do at session start. Only reset if we had actually
    // identified someone.
    if (hasIdentified) {
      push(['resetIdentity']);
      hasIdentified = false;
    }
    return;
  }

  // Internal staff are never recorded. `optout` sets a persistent `_cs_optout`
  // cookie and clears the other Contentsquare cookies, so from the next page
  // load on this browser the tag records nothing at all. (The tag loads on
  // mount before auth resolves, so a few pre-identify frames of the very first
  // load can still slip through — acceptable for staff.) We also send no
  // identify/PII for them. This is a hard collection-side exclusion, separate
  // from the analysis-side `internal` flag used for impersonation replays.
  if (isInternalEmail(user.email)) {
    pushSuppression(['optout']);
    // We are no longer identified. Keep the reset guard honest so a later
    // logout can't push a needless resetIdentity against an opted-out session.
    hasIdentified = false;
    return;
  }

  // Reverse any prior opt-out before recording a real (non-internal) user.
  // `optout` is sticky — a persistent cookie — so without this, a browser that
  // ever saw a staff login stays silently unrecorded. The load-bearing case is
  // impersonation: an admin browses as themselves (opts out), then impersonates
  // a customer — the session we deliberately DO want to record (internal flag)
  // would be suppressed by the lingering cookie. `optin` is a no-op when
  // tracking is already enabled, so paying it on every real identify is cheap.
  push(['optin']);

  // Vendor only for real processor accounts — see IdentifyOptions.includeVendor.
  const vendorId = includeVendor ? user.active_vendor?._id : undefined;
  const vendorName = includeVendor ? user.active_vendor?.shop_name : undefined;

  // Contact fields for Support search. Phone → digits only, so a stored
  // "(555) 123-4567" matches a searched "5551234567". Name is joined safely so
  // an absent part never becomes the literal "undefined".
  const email = user.email ?? '';
  const phone = (user.phone ?? '').replace(/\D/g, '');
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  const company = user.company ?? '';

  push(['identify', user._id]);

  // Contentsquare identify is STATEFUL: user properties persist on the user,
  // and the visit-scoped custom variables below persist across the visit until
  // overwritten. So we send the COMPLETE current state every time and clear a
  // now-absent field with an empty string rather than omitting it. Omitting was
  // the earlier design, but it leaves a stale value from a prior identity — e.g.
  // after an admin stops impersonating a producer, `userType` kept reading
  // `producer` on the admin's continued session because the update was skipped.
  // `?? ''` also guarantees we never emit the literal string "undefined" (the
  // Lucky Orange interpolation bug).
  const properties: Record<string, string | boolean> = {
    userId: user._id,
    internal: impersonating,
    role: user.role ?? '',
    vendorId: vendorId ?? '',
    vendorName: vendorName ?? '',
    userType: userType ?? '',
    email,
    phone,
    name,
    company,
  };

  push(['addUserProperties', properties]);
  hasIdentified = true;

  setCustomVariable(CUSTOM_VAR.userId, 'userId', user._id);
  setCustomVariable(CUSTOM_VAR.role, 'role', user.role);
  setCustomVariable(CUSTOM_VAR.vendorId, 'vendorId', vendorId);
  setCustomVariable(CUSTOM_VAR.vendorName, 'vendorName', vendorName);
  // Custom variables are strings; the user property above keeps the boolean.
  setCustomVariable(CUSTOM_VAR.internal, 'internal', String(impersonating));
  setCustomVariable(CUSTOM_VAR.userType, 'userType', userType);
  setCustomVariable(CUSTOM_VAR.email, 'email', email);
  setCustomVariable(CUSTOM_VAR.phone, 'phone', phone);
  setCustomVariable(CUSTOM_VAR.name, 'name', name);
  setCustomVariable(CUSTOM_VAR.company, 'company', company);
}

// NOTE: Contentsquare's purpose-built User Identifier search — a
// `trackPageEvent('@user-identifier@<value>')` page event — is Pro/Enterprise
// only and is deliberately NOT sent here. We are on Growth, where it would just
// create a high-cardinality junk page event per user. If we ever move to Pro,
// this one call (on identify, after `identify`) turns on the type-and-go search.
// On Growth, per-user lookup goes through custom-variable segments instead.

/**
 * Sets a visit-scoped custom variable, sending an empty string for an absent
 * value so it CLEARS any value left by a prior identity in the same visit
 * (see the stateful-identify note above) — never skips, or the stale value
 * persists. `?? ''` keeps the literal "undefined" out of the payload.
 */
function setCustomVariable(
  index: number,
  name: string,
  value?: string | null,
): void {
  push(['setCustomVariable', index, name, value ?? '', SCOPE_VISIT]);
}

/**
 * Generates a shareable replay link for the current session, for pasting into a
 * Linear or Intercom ticket so a bug report carries proof. Resolves to null
 * when Contentsquare is disabled, when this visitor is not being recorded at
 * all (internal staff, or an anonymous visitor outside the sample), or when no
 * session is recording yet.
 *
 * The `hasRequestedLoad` check is what keeps that null honest: the result
 * arrives through a callback the tag invokes, so with no tag there is nothing
 * to invoke it and the promise would hang forever rather than resolve.
 *
 * This is the self-diagnosis workflow Support asked for — see PRO-638 #6.
 */
export function generateContentsquareReplayLink(): Promise<string | null> {
  if (!isContentsquareEnabled()) return Promise.resolve(null);
  if (!hasRequestedLoad) return Promise.resolve(null);

  return new Promise((resolve) => {
    // Requesting the tag is not the same as getting it: an ad blocker or a CSP
    // can drop the script after injection, and then nothing ever invokes the
    // callback below. Without a deadline the promise simply never settles, and
    // Support is left holding a button that spins forever — for exactly the
    // users most likely to be running a blocker. Resolving null is the same
    // answer they would get from any other not-recording session.
    const timeout = setTimeout(() => resolve(null), REPLAY_LINK_TIMEOUT_MS);

    push([
      'replay:link:generate',
      { withTimestamp: true },
      (result: { isRecording?: boolean; replayLink?: string }) => {
        clearTimeout(timeout);
        resolve(result?.replayLink ?? null);
      },
    ]);
  });
}
